import React from 'react';
import { FaPencilAlt, FaTrash, FaLock, FaUserCheck, FaUnlockAlt, FaUserShield, FaEnvelope, FaPhone } from 'react-icons/fa';

export const parseIsReleased = (val) => (val === true || val === 1 || val === '1' || val === 'true');

const TeamMemberTable = ({ teamMembers, onEdit, onDelete }) => {
  const renderPrivilegePills = (permissions, isMobile = false) => {
    const modulesMap = {};
    (permissions || []).forEach(p => {
      const [mod, act] = p.split(':');
      if (!modulesMap[mod]) modulesMap[mod] = [];
      modulesMap[mod].push(act);
    });

    if (Object.keys(modulesMap).length === 0) return null;

    if (isMobile) {
      return (
        <div className="mtm-card-privileges mt-2">
          <div className="text-muted small fw-bold mb-1" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Privileges Matrix:
          </div>
          <div className="mtm-pill-scroll-container">
            {Object.entries(modulesMap).map(([mod, actions]) => (
              <div key={mod} className="mtm-h-pill">
                <span className="mtm-h-pill-title">{mod}:</span>
                <span className="mtm-h-pill-body">{actions.join(', ')}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="mtm-pill-container mtm-desktop-pill-scroll">
        {Object.entries(modulesMap).map(([mod, actions]) => (
          <div key={mod} className="mtm-pill">
            <span className="mtm-pill-title">{mod}</span>
            <span className="mtm-pill-body">{actions.join(', ')}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mtm-card-box p-0 border-0 background-transparent">
      {/* DESKTOP NEAT TABLE VIEW */}
      <div className="table-responsive d-none d-md-block rounded-3 overflow-hidden border border-secondary border-opacity-25" style={{ background: '#0e1017' }}>
        <table className="mtm-data-table m-0">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>#</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Designation</th>
              <th>Privileges</th>
              <th>Login</th>
              <th>Status</th>
              <th style={{ textAlign: 'center', width: '100px' }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {teamMembers.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-5 text-muted">
                  <FaUserShield size={36} className="mb-2 opacity-50 text-danger" />
                  <p className="m-0 fw-bold">No team members added yet.</p>
                </td>
              </tr>
            ) : (
              teamMembers.map((member, index) => {
                const isReleased = parseIsReleased(member.is_released !== undefined ? member.is_released : member.isReleased);
                return (
                  <tr key={index}>
                    <td className="text-muted fw-bold">{index + 1}</td>
                    <td>
                      <div className="fw-bold text-white fs-6">{member.name}</div>
                      <small className="text-muted d-block">{member.contactType?.label || member.contactType || 'Staff'}</small>
                    </td>
                    <td className="text-white-50">{member.email}</td>
                    <td><span className="mtm-designation-badge">{member.designation?.label || member.designation || 'N/A'}</span></td>
                    <td>{renderPrivilegePills(member.permissions, false)}</td>
                    <td>
                      {member.enableLogin ? <span className="pmp-status-tag pmp-status-active"><FaLock size={10} className="me-1" /> Enabled</span> : <span className="pmp-status-tag pmp-status-inactive">Disabled</span>}
                    </td>
                    <td>
                      {isReleased ? <span className="pmp-status-tag pmp-status-expired"><FaUnlockAlt size={10} className="me-1" /> Released</span> : <span className="pmp-status-tag pmp-status-active"><FaUserCheck size={10} className="me-1" /> Active</span>}
                    </td>
                    <td>
                      <div className="d-flex justify-content-center align-items-center gap-2">
                        <button type="button" className="mtm-icon-btn mtm-edit" title="Edit Member" onClick={() => onEdit(index)}>
                          <FaPencilAlt size={12} color="#ffffff" />
                        </button>
                        <button type="button" className="mtm-icon-btn mtm-delete" title="Delete Member" onClick={() => onDelete(index)}>
                          <FaTrash size={12} color="#ffffff" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* MOBILE PROJECT-STYLE CARDS VIEW */}
      <div className="mtm-mobile-cards d-md-none d-flex flex-column gap-3">
        {teamMembers.length === 0 ? (
          <div className="text-center py-5 text-muted bg-dark rounded p-4 border border-secondary border-opacity-25">
            <FaUserShield size={36} className="mb-2 opacity-50 text-danger" />
            <p className="m-0 fw-bold text-white">No team members added yet.</p>
          </div>
        ) : (
          teamMembers.map((member, index) => {
            const isReleased = parseIsReleased(member.is_released !== undefined ? member.is_released : member.isReleased);
            return (
              <div key={index} className="project-mobile-card">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="flex-grow-1 min-w-0 me-2">
                    <h6 className="project-card-title m-0 text-white fw-bold text-truncate" style={{ fontSize: '15px' }}>{member.name}</h6>
                    <span className="mtm-designation-badge mt-1 d-inline-block">{member.designation?.label || member.designation || 'Staff'}</span>
                  </div>
                  <span className={`pmp-status-tag flex-shrink-0 ${isReleased ? 'pmp-status-expired' : 'pmp-status-active'}`}>
                    {isReleased ? 'Released' : 'Active'}
                  </span>
                </div>

                <div className="card-data-grid mt-2 mb-2">
                  <div className="data-item"><FaEnvelope className="text-danger" /><span className="lbl">Email:</span><span className="val text-truncate" style={{ maxWidth: '110px' }}>{member.email}</span></div>
                  <div className="data-item"><FaPhone className="text-success" /><span className="lbl">Mobile:</span><span className="val">{member.mobile || 'N/A'}</span></div>
                  <div className="data-item"><FaLock className="text-warning" /><span className="lbl">Login:</span><span className="val">{member.enableLogin ? 'Enabled' : 'Disabled'}</span></div>
                  <div className="data-item"><FaUserShield className="text-info" /><span className="lbl">Type:</span><span className="val">{member.contactType?.label || member.contactType || 'Staff'}</span></div>
                </div>

                {renderPrivilegePills(member.permissions, true)}
                <hr className="compact-hr my-2" />

                <div className="d-flex justify-content-between align-items-center">
                  <div className="house-data"><span className="house-name-gold">Member #{index + 1}</span></div>
                  <div className="pmp-action-btn-group d-flex gap-2">
                    <button type="button" className="mtm-icon-btn mtm-edit" title="Edit Member" onClick={() => onEdit(index)}><FaPencilAlt size={12} color="#ffffff" /></button>
                    <button type="button" className="mtm-icon-btn mtm-delete" title="Delete Member" onClick={() => onDelete(index)}><FaTrash size={12} color="#ffffff" /></button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TeamMemberTable;
